import { NgZone } from '@angular/core';
import * as _ from 'lodash';
import { AreasWithStackingGroup, Position, Group } from 'src/app/core/types';
import { Object3D, Vector3, Group as ThreeGroup, Box3, Box3Helper, Color } from 'three';
import { PerspectiveCamera } from 'three';
import { Area, AreaType } from './area';
import { AreasCalculator } from './areas-calculator';
import { Load } from './load';
import { LoadActions, LoadActionEvent } from './load-actions';
import { LoadSnapper } from './load-snapper';
import { LoadFactory } from './loadFactory';
import { LoadingSpace } from './loading-space';
import { Outside } from './outside';
import RenderingContext from './rendering-context';
import { Unloading } from './unloading';
import { CookieService } from 'ngx-cookie-service';
import { calculateRotatedObjectData } from './utils/boundingBoxData';
import { ProjectService } from '../../core/services/project.service';
import { MainView } from './main-view';
import {
  calculateSlopeOffsets,
  detectCollisionsOnSlope,
  isItemOnSlope,
  ItemOnSlope,
} from './utils/loadDragAirContainer';
// import { MainView } from './main-view';
import { illuminationBoxesForAreas } from './load-drag-illumination';
import { shouldRestoreLoadOpacityAfterDrag } from './loads-groups-drawer';
import { loadAabbsOverlap, LoadAabb } from './load-overlap';
import {
  calculateVarioAreaHeight,
  filterStackingGroups,
  filterVarioAreas,
  isLoadOnSemiTrailer,
  isLoadOnTrailer,
  setStackedForVarioFloor,
} from './vario-floor-functions';
import { environment } from '../../../environments/environment';

export class LoadDrag {
  public isSnappingActive: boolean;
  private draggedLoadPosition: Position;
  public loadSnapper: LoadSnapper;
  private dragControls: LoadActions;
  private areasCalculator: AreasCalculator;
  private areasThatCanFitLoad: Area[];
  private recalculatedAreas: Area[];
  private areasTemporary: Area[];
  private illuminationGroup: ThreeGroup;
  private oldPosition: Vector3;
  private isTest = false;

  constructor(
    private renderingContext: RenderingContext<PerspectiveCamera>,
    private areas: Area[],
    private loads: Load[],
    private loadingSpace: LoadingSpace,
    private outside: Outside,
    private unloading: Unloading,
    private groups: Group[],
    private areasWithStackingGroups: AreasWithStackingGroup[],
    private zone: NgZone,
    private cookieService: CookieService,
    private projectService: ProjectService,
    private mainView: MainView,
  ) {
    this.loadSnapper = new LoadSnapper();
    this.areasCalculator = new AreasCalculator();
    this.dragControls = new LoadActions(
      loads,
      this.renderingContext,
      this.loadingSpace,
      this.unloading,
      this.zone,
      this.cookieService,
      this.projectService,
    );
    this.addDragControls();
    this.activateSnapper();
    if (environment.isThreeStats) {
      this.isTest = true;
    }
  }

  private blinkingBoundingBox(load: Load, color: any = 0xffc0cb): void {
    this.renderingContext.scene.remove(this.renderingContext.scene.getObjectByName('blinkBox'));
    const box: Box3 = new Box3().setFromObject(load);
    const box3Helper: Box3Helper = new Box3Helper(box, color);
    box3Helper.name = 'blinkBox';
    this.renderingContext.scene.add(box3Helper);
  }

  public update(
    loads: Load[],
    areas: Area[],
    loadingSpace: LoadingSpace,
    outside: Outside,
    unloading: Unloading,
    groups: Group[],
    areasWithStackingGroups: AreasWithStackingGroup[],
  ): void {
    this.loadingSpace = loadingSpace;
    this.loads = loads;
    this.areas = areas;
    this.dragControls.updateLoads(loads);
    this.dragControls.updateLoadingSpace(loadingSpace);
    this.outside = outside;
    this.unloading = unloading;
    this.groups = groups;
    this.areasWithStackingGroups = areasWithStackingGroups;
  }

  public activateSnapper(): void {
    this.isSnappingActive = true;
  }

  public deactivateSnapper(): void {
    this.isSnappingActive = false;
  }

  public activateDrag(): void {
    this.dragControls.activate();
  }

  public deactivateDrag(): void {
    this.dragControls.deactivate();
  }

  public addDragControls(): void {
    this.dragControls.addEventListener('dragstart', (event: LoadActionEvent) => this.onDragStart(event));
    this.dragControls.addEventListener('drag', (event: LoadActionEvent) => this.onDrag(event));
    this.dragControls.addEventListener('dragend', (event: LoadActionEvent) => this.onDragEnd(event));
    this.dragControls.addEventListener('actions', (event: LoadActionEvent) => this.onContextMenu(event));
  }

  private changeBarrelFloorAreas(areas: Area[]): Area[] {
    const modifiedBarrelAreas: Area[] = areas.map((area: Area): Area => {
      if (area.type !== 1 && area.y !== 0) {
        return area;
      }
      if (area.type === 1 && area.y === 0) {
        const modifiedArea: Area = new Area(
          area.length,
          area.width,
          area.height,
          area.x,
          area.y,
          area.z,
          AreaType.barrelType,
        );
        return modifiedArea;
      }
      return area;
    });
    return modifiedBarrelAreas;
  }

  private onDragStart(event: LoadActionEvent): void {
    const originalObject: Load = event.object;
    // event.object.position.x = _.round(event.object.position.x);
    // event.object.position.y = _.round(event.object.position.y);
    // event.object.position.z = _.round(event.object.position.z);
    this.draggedLoadPosition = {
      x: event.object.oldPosition.x,
      y: event.object.oldPosition.y,
      z: event.object.oldPosition.z,
    };
    this.oldPosition = event.object.position.clone();
    const isRotated: number = originalObject.yRotation || originalObject.edgeRotation;
    const isVarioFloor: number =
      this.loadingSpace.varioTrailerFloorHeight || this.loadingSpace.varioSemiTrailerFloorHeight;
    if (isVarioFloor) {
      setStackedForVarioFloor(this.loads);
    }
    let areas: Area[] = this.areas;
    if (isVarioFloor) {
      const isLoadOnTrailerVarioFloor: boolean =
        isLoadOnTrailer(event.object, this.loadingSpace) &&
        event.object.position.y >= this.loadingSpace.varioTrailerFloorHeight;
      const isLoadOnSemiTrailerVarioFloor: boolean =
        isLoadOnSemiTrailer(event.object, this.loadingSpace) &&
        event.object.position.y >= this.loadingSpace.varioSemiTrailerFloorHeight;
      areas = filterVarioAreas(
        areas,
        this.loadingSpace,
        isLoadOnTrailerVarioFloor,
        isLoadOnSemiTrailerVarioFloor,
      );
      this.areasTemporary = areas;
    } else {
      this.areasTemporary = areas;
    }
    this.renderingContext.cameraControls.enabled = false;
    let areasWithStackingGroups: AreasWithStackingGroup[] = this.areasWithStackingGroups;
    if (isVarioFloor) {
      const isLoadOnTrailerVarioFloor: boolean =
        isLoadOnTrailer(event.object, this.loadingSpace) &&
        event.object.position.y >= this.loadingSpace.varioTrailerFloorHeight;
      const isLoadOnSemiTrailerVarioFloor: boolean =
        isLoadOnSemiTrailer(event.object, this.loadingSpace) &&
        event.object.position.y >= this.loadingSpace.varioSemiTrailerFloorHeight;
      areasWithStackingGroups = filterStackingGroups(
        this.areasWithStackingGroups,
        this.loadingSpace,
        isLoadOnTrailerVarioFloor,
        isLoadOnSemiTrailerVarioFloor,
      );
    }
    if (!isVarioFloor) {
      this.areasWithStackingGroups.forEach((stackingGroup: AreasWithStackingGroup): void => {
        if (stackingGroup.id === event.object.idGroup) {
          stackingGroup.areas.forEach((area: Area): void => {
            this.areas = this.areas.filter((a: Area): boolean => a !== area);
          });
        }
      });
    }

    const isBarrels: boolean =
      this.loads.find((load: Load): boolean => load.loadType === 1) !== undefined;
    this.recalculatedAreas = this.areasCalculator.recalculateAreasAfterLoadPickUp(
      event.object,
      this.loadingSpace,
      this.outside,
      areas,
      this.groups,
      areasWithStackingGroups,
      this.loads,
    );
    if (event.object.allowOutOfSpace) {
      this.createAreasForProtrudingLoad(event.object);
    }
    const isCurrentLoadABarrel: boolean = event.object.loadType === 1;
    if (isBarrels && !isCurrentLoadABarrel) {
      this.recalculatedAreas = this.turnOffAllBarrelAreas(this.loads, this.recalculatedAreas);
    }
    if (isBarrels && isCurrentLoadABarrel) {
      this.recalculatedAreas = this.changeBarrelFloorAreas(areas);
      this.recalculatedAreas = this.turnOffAllBarrelAreas(this.loads, this.recalculatedAreas);
      this.recalculatedAreas = this.generateBarrelAreas(
        this.loads,
        this.recalculatedAreas,
        event.object,
      );
      const barrelArea: Area = new Area(
        originalObject.length,
        originalObject.width,
        this.loadingSpace.height - originalObject.height,
        originalObject.position.x,
        originalObject.position.y,
        originalObject.position.z,
        0,
      );
      this.recalculatedAreas.push(barrelArea);
    }

    if (isRotated) {
      const rotatedObjectData: Partial<Load> = calculateRotatedObjectData(event.object.parent);
      this.areasThatCanFitLoad = this.recalculatedAreas.filter((area: Area): boolean => {
        if (
          rotatedObjectData.position.x + rotatedObjectData.width / 2 >= area.x &&
          rotatedObjectData.position.z + rotatedObjectData.length / 2 >= area.z &&
          rotatedObjectData.position.x + rotatedObjectData.width / 2 <= area.x2() &&
          rotatedObjectData.position.z + rotatedObjectData.length / 2 <= area.z2()
        ) {
          return true;
        }
        return area.canFitLoad(rotatedObjectData as Load);
      });
    } else {
      this.areasThatCanFitLoad = this.recalculatedAreas.filter((area: Area) => {
        return area.canFitLoad(event.object);
      });
    }
    if (this.isTest) {
      this.addFreeSpacesHelperBoxes(this.recalculatedAreas, this.mainView);
    }
    // this.areasTemporary = this.areas;

    if (this.cookieService.get('illuminationLoadPlaces').includes('true')) {
      const areasThatCanFitLoadInLoadingSpace: Area[] = this.areasThatCanFitLoad.filter(
        (area: Area) =>
          this.loadingSpace.isInFirstPart(new Vector3(area.x, area.y, area.z)) ||
          this.loadingSpace.isInSecondPart(new Vector3(area.x, area.y, area.z)),
      );
      this.illuminationGroup = illuminationBoxesForAreas(
        areasThatCanFitLoadInLoadingSpace,
        this.renderingContext.scene,
      );
    }
  }

  private createAreasForProtrudingLoad(object: Load): void {
    const objectMaxOutOfSpaceLength: number = object.length * 0.3;
    const maxOutOfSpaceLength = 20000;
    const allowedOutOfSpaceLength: number =
      objectMaxOutOfSpaceLength <= maxOutOfSpaceLength
        ? objectMaxOutOfSpaceLength
        : maxOutOfSpaceLength;
    this.recalculatedAreas = this.extendAreasByLength(
      this.recalculatedAreas,
      allowedOutOfSpaceLength,
    );
    this.recalculatedAreas = this.extendLoadOnEndArea(
      object,
      this.recalculatedAreas,
      allowedOutOfSpaceLength,
    );
  }

  private extendAreasByLength(areas: Area[], length: number): Area[] {
    const areasOnEnd: Area[] = areas.filter(
      (area: Area) =>
        area.z2() === this.loadingSpace.length &&
        area.x2() <= this.loadingSpace.width &&
        area.x >= 0 &&
        area.y === 0,
    );
    const extendedAreas: Area[] = areasOnEnd.map((area: Area) => {
      return new Area(area.length + length, area.width, area.height, area.x, area.y, area.z, 0);
    });
    return [...areas, ...extendedAreas];
  }

  private turnOffAllBarrelAreas(loads: Load[], areas: Area[]): Area[] {
    const loadsBarrels: Load[] = loads.filter((load: Load): boolean => load.loadType === 1);
    const filteredAreas: Area[] = areas.filter((area: Area): boolean => {
      const loadUnder: Load = loadsBarrels.find(
        (load: Load) =>
          load.position.y + load.height === area.y &&
          load.position.x <= area.x &&
          load.position.x + load.width >= area.x2() &&
          load.position.z <= area.z &&
          load.position.z + load.length >= area.z2(),
      );
      const loadUnder2: Load = loadsBarrels.find(
        (load: Load) =>
          load.position.y + load.height === area.y &&
          load.position.x + load.width / 2 >= area.x &&
          load.position.x + load.width / 2 <= area.x2(),
      );
      if (loadUnder !== undefined || loadUnder2 !== undefined) {
        return false;
      }
      return true;
    });
    return filteredAreas;
  }

  private isLoadOverAnotherLoad(load1: Load, load2: Load): boolean {
    return (
      load1.y + load1.height === load2.y &&
      load1.id !== load2.id &&
      load1.position.x <= load2.position.x &&
      load1.position.x + load1.width >= load2.position.x + load2.width &&
      load1.position.z <= load2.position.z &&
      load1.position.z + load1.length >= load2.position.z + load2.length &&
      load1.loadType === 1
    );
  }

  private generateBarrelAreas(loads: Load[], areas: Area[], currentLoad: Load): Area[] {
    const findAllBarrelsCovered: Load[] = loads.filter((l: Load) => {
      const isLoadOver: Load = loads.find((l2: Load) => this.isLoadOverAnotherLoad(l, l2));
      const isCurrentLoadOver: boolean = this.isLoadOverAnotherLoad(l, currentLoad);
      return isCurrentLoadOver || isLoadOver === undefined;
    });
    const isVarioFloor: boolean = this.loadingSpace.varioTrailerFloorHeight !== undefined;
    const isSetVarioFloor: boolean = this.loadingSpace.varioSemiTrailerFloorHeight !== undefined;
    const createBarrelAreas: Area[] = findAllBarrelsCovered.reduce(
      (acu: Area[], barrelLoad: Load) => {
        let barrelAreaHeight: number =
          this.loadingSpace.height - (barrelLoad.y + barrelLoad.height);
        if (isVarioFloor || isSetVarioFloor) {
          barrelAreaHeight = calculateVarioAreaHeight(barrelLoad, this.loadingSpace);
        }
        const sideLength: number = Math.sqrt(2 * Math.pow(barrelLoad.diameter / 2, 2));
        const area: Area = new Area(
          sideLength,
          sideLength,
          barrelAreaHeight,
          barrelLoad.position.x + barrelLoad.width / 2 - sideLength / 2,
          barrelLoad.position.y + barrelLoad.height,
          barrelLoad.position.z + barrelLoad.length / 2 - sideLength / 2,
          AreaType.barrelType,
        );
        if (barrelLoad.stacking && barrelLoad.id !== currentLoad.id) {
          acu.push(area);
        }
        return acu;
      },
      [],
    );
    return [...areas, ...createBarrelAreas];
  }

  private addFreeSpacesHelperBoxes(areas: Area[], mainView: MainView): void {
    areas.forEach((area: Area): void => {
      const boxMin: Vector3 = new Vector3(area.x, area.y, area.z);
      const boxMax: Vector3 = new Vector3(area.x + area.width, area.y + 10, area.z + area.length);
      const boxHelper: Box3 = new Box3(boxMin, boxMax);
      const helperBox2: Box3Helper = new Box3Helper(boxHelper, this.getRandomColor());
      helperBox2.name = 'freeSpaceHelper';
      mainView.renderingContext.scene.add(helperBox2);
    });
  }

  private getRandomColor(): Color {
    const colors: Color[] = [
      new Color(0xff0000),
      new Color(0x00ff00),
      new Color(0x0000ff),
      new Color(0xffff00),
      new Color(0x7ae7ff),
      new Color(0xd416fa),
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  private extendLoadOnEndArea(load: Load, areas: Area[], length: number): Area[] {
    const isLoadOnEnd: boolean = load.z + load.length >= this.loadingSpace.length;
    const isLoadOnFloor: boolean = load.y === 0;
    if (!isLoadOnEnd || !isLoadOnFloor) {
      return areas;
    }
    const areasBeforeLoad: Area[] = areas.filter((area: Area) => {
      return (
        area.z2() <= load.z &&
        ((load.x >= area.x && load.x <= area.x2()) ||
          (load.x + load.width >= area.x && load.x + load.width <= area.x2()) ||
          (load.x <= area.x && load.x + load.width >= area.x2())) &&
        area.z >= 0 &&
        area.x >= 0 &&
        area.x2() <= this.loadingSpace.width
      );
    });
    areasBeforeLoad.sort((areaA: Area, areaB: Area) => {
      return areaB.z2() - areaA.z2();
    });
    const loadsBeforeLoad: Load[] = this.loads
      .filter((load3: Load): boolean => load3.id !== load.id)
      .filter((load2: Load) => {
        return (
          load2.z + load2.length <= load.z &&
          ((load2.x >= load.x && load2.x <= load.x + load.width) ||
            (load2.x + load2.width >= load.x && load2.x + load2.width <= load.x + load.width) ||
            (load2.x <= load.x && load2.x + load2.width >= load.x + load.width))
        );
      });
    loadsBeforeLoad.sort((loadA: Load, loadB: Load) => {
      return loadB.z - loadA.z;
    });
    let lastLoadPosition = 0;
    let lastAreaPosition = 0;
    let lastZPosition = 0;
    let totalLength = 0;
    if (loadsBeforeLoad.length !== 0 && areasBeforeLoad.length !== 0) {
      lastLoadPosition = loadsBeforeLoad[0].z + loadsBeforeLoad[0].length;
      lastAreaPosition = areasBeforeLoad[0].z2();
    }
    if (loadsBeforeLoad.length === 0 && areasBeforeLoad.length !== 0) {
      lastAreaPosition = areasBeforeLoad[0].z2();
    }
    if (loadsBeforeLoad.length !== 0 && areasBeforeLoad.length === 0) {
      lastLoadPosition = loadsBeforeLoad[0].z + loadsBeforeLoad[0].length;
    }
    lastZPosition = lastLoadPosition > lastAreaPosition ? lastLoadPosition : lastAreaPosition;
    totalLength = !lastZPosition
      ? this.loadingSpace.length + length
      : this.loadingSpace.length - lastZPosition + length;
    const loadArea: Area = new Area(
      totalLength,
      load.width,
      this.loadingSpace.height,
      load.x,
      load.y,
      lastZPosition,
    );
    return [...areas, loadArea];
  }

  private getOffsetOfLoadOnXZInCircleArea(
    load: Load,
    barrelUnder: Load,
    area?: Area,
  ): [number, number] {
    const areaData =
      area !== undefined
        ? {
            x: area.x + area.width / 2,
            z: area.z + area.length / 2,
            r: area.width / 2,
          }
        : {
            x: barrelUnder.x + barrelUnder.width / 2,
            z: barrelUnder.z + barrelUnder.length / 2,
            r: barrelUnder.width / 2,
          };
    const withPivot: Load = load.name?.includes('pivot') ? (load.children[0] as Load) : load;
    let loadCenter: {
      x: number;
      z: number;
      freedomRadius: number;
    };
    if (withPivot.loadType === 1) {
      loadCenter = {
        x: withPivot.position.x + withPivot.diameter / 2,
        z: withPivot.position.z + withPivot.diameter / 2,
        freedomRadius: areaData.r - withPivot.diameter / 2,
      };
    } else {
      loadCenter = {
        x: withPivot.position.x + (withPivot.width | withPivot.diameter) / 2,
        z: withPivot.position.z + (withPivot.length | withPivot.diameter) / 2,
        freedomRadius:
          areaData.r -
          Math.sqrt(
            Math.pow(withPivot.width | withPivot.diameter, 2) +
              Math.pow(withPivot.length | withPivot.diameter, 2),
          ) /
            2,
      };
    }

    const distX: number = areaData.x - loadCenter.x;
    const distZ: number = areaData.z - loadCenter.z;
    const directDisSqr: number = Math.pow(distX, 2) + Math.pow(distZ, 2);

    if (directDisSqr <= Math.pow(loadCenter.freedomRadius, 2)) {
      return [0, 0];
    } else {
      return [
        (-distX * (Math.sqrt(directDisSqr) - loadCenter.freedomRadius)) / Math.sqrt(directDisSqr),
        (-distZ * (Math.sqrt(directDisSqr) - loadCenter.freedomRadius)) / Math.sqrt(directDisSqr),
      ];
    }
  }

  private findBarrelUnderLoad(originalObject: Load, area: Area): Load {
    return this.loads
      .filter((load: Load): boolean => load.loadType === 1)
      .find((load: Load) => {
        return (
          area.x >= load.position.x &&
          area.x2() <= load.position.x + load.width &&
          area.z >= load.position.z &&
          area.z2() <= load.position.z + load.length &&
          area.y === load.position.y + load.height &&
          originalObject.id !== load.id
        );
      });
  }

  private isBarrelOccupied(barrelUnderLoad: Load, originalObject: Load): Load {
    return this.loads
      .filter((load: Load) => load.loadType === 1 && load.id !== originalObject.id)
      .find((load: Load) => {
        return (
          barrelUnderLoad.position.x + barrelUnderLoad.width / 2 > load.position.x &&
          barrelUnderLoad.position.x + barrelUnderLoad.width / 2 < load.position.x + load.length &&
          barrelUnderLoad.position.z + barrelUnderLoad.length / 2 > load.position.z &&
          barrelUnderLoad.position.z + barrelUnderLoad.length / 2 < load.position.z + load.length &&
          barrelUnderLoad.y + barrelUnderLoad.height === load.position.y &&
          barrelUnderLoad.id !== load.id
        );
      });
  }

  public filterOutArea(areaToFilterOut: Area): void {
    this.areasThatCanFitLoad = this.areasThatCanFitLoad.filter(
      (area: Area) => !this.areAreasEqual(area, areaToFilterOut),
    );
  }

  private areAreasEqual(area1: Area, area2: Area): boolean {
    return (
      area1.x === area2.x &&
      area1.y === area2.y &&
      area1.z === area2.z &&
      area1.length === area2.length &&
      area1.width === area2.width &&
      area1.height === area2.height
    );
  }

  private onDrag(event: LoadActionEvent): void {
    const isAirContainer: boolean = this.loadingSpace.type === 4;
    const originalObject: Load = event.object;
    const isRotated: number = event.object.yRotation || event.object.edgeRotation;
    const isBarrel: boolean = originalObject.loadType === 1;
    const area: Area = this.areasThatCanFitLoad?.[0];
    if (!area) {
      if (!isRotated) {
        this.restoreDraggedPosition(event.object);
      }
      return;
    }
    const rotatedData: Partial<Load> = isRotated
      ? calculateRotatedObjectData(event.object.parent.parent)
      : event.object;
    this.sortAreasByDistance(rotatedData as Load);
    let offsetOnX = 0;
    let offsetOnZ = 0;
    let barrelUnderLoad: Load = undefined;
    let isBarrelOccupied = undefined;
    if (isBarrel) {
      barrelUnderLoad = this.findBarrelUnderLoad(originalObject, area);
      if (barrelUnderLoad) {
        isBarrelOccupied = this.isBarrelOccupied(barrelUnderLoad, originalObject);
      }
    }
    if (
      isBarrel &&
      barrelUnderLoad &&
      isBarrelOccupied === undefined &&
      area.y > 0 &&
      barrelUnderLoad.stacking
    ) {
      [offsetOnX, offsetOnZ] = this.getOffsetOfLoadOnXZInCircleArea(
        rotatedData as Load,
        barrelUnderLoad,
      );
    } else if (area.type && area.type === 5 && area.y === 0) {
      [offsetOnX, offsetOnZ] = this.getOffsetOfLoadOnXZInCircleArea(
        rotatedData as Load,
        barrelUnderLoad,
        area,
      );
    } else {
      offsetOnZ = offsetOnZ + this.getOffsetOfLoadOnZInArea(rotatedData as Load, area);
      offsetOnX = offsetOnX + this.getOffsetOfLoadOnXInArea(rotatedData as Load, area);
    }

    const isPivot: boolean = event.object.parent.name.includes('pivot');
    const eventObject: Object3D = isPivot ? event.object.parent.parent : event.object;
    const xPositionWithOffset: number = eventObject.position.x - offsetOnX;
    const zPositionWithOffset: number = eventObject.position.z - offsetOnZ;
    eventObject.position.set(xPositionWithOffset, area.y, zPositionWithOffset);

    if (isAirContainer && area.y === 0) {
      calculateSlopeOffsets(event.object, area, this.loadingSpace);
      const isAreaOnSlope: ItemOnSlope = isItemOnSlope(area, this.loadingSpace);
      const isInCollision: boolean = detectCollisionsOnSlope(
        eventObject as Load,
        this.loads,
        this.mainView,
        eventObject.id,
        this.loadingSpace,
      );
      if (isInCollision) {
        this.filterOutArea(area);
      }
      if (
        isInCollision &&
        this.oldPosition &&
        (isAreaOnSlope.isItemOnLeftSlope || isAreaOnSlope.isItemOnRightSlope)
      ) {
        eventObject.position.setX(this.oldPosition.x);
        eventObject.position.setZ(this.oldPosition.z);
        eventObject.position.setY(this.oldPosition.y);
      }
      if (!isInCollision) {
        this.oldPosition = eventObject.position.clone();
      }
    }

    if (isPivot) {
      event.object.parent.position.setY(0);
      event.object.position.setY(0);
    }
    if (
      this.isSnappingActive &&
      !this.loadSnapper.isDraggedLoadInCenterOfArea(event.object, area)
    ) {
      const snappedPosition: Vector3 = this.loadSnapper.isAnyLoadCornerNearDraggedLoad(
        event.object,
        this.loads,
        this.areasThatCanFitLoad,
        this.draggedLoadPosition as Vector3,
      );
      if (snappedPosition) {
        event.object.position.x = snappedPosition.x;
        event.object.position.z = snappedPosition.z;
      }
    }
    if (!isRotated && !isAirContainer && this.dragOverlapsAnotherLoad(originalObject, eventObject)) {
      this.restoreDraggedPosition(event.object);
    } else if (!isAirContainer && this.oldPosition) {
      this.oldPosition = eventObject.position.clone();
    }
    if (this.isTest) {
      this.blinkingBoundingBox(isRotated ? (event.object.parent.parent as Load) : originalObject);
    }
  }

  private restoreDraggedPosition(load: Load): void {
    if (!this.oldPosition) {
      return;
    }
    load.position.copy(this.oldPosition);
  }

  private dragOverlapsAnotherLoad(dragged: Load, eventObject: Object3D): boolean {
    const proposed: LoadAabb = {
      x: eventObject.position.x,
      y: eventObject.position.y,
      z: eventObject.position.z,
      width: dragged.width,
      length: dragged.length,
      height: dragged.height,
    };
    return this.loads.some((other) => {
      if (other === dragged || other === eventObject) {
        return false;
      }
      return loadAabbsOverlap(proposed, {
        x: other.position.x,
        y: other.position.y,
        z: other.position.z,
        width: other.width,
        length: other.length,
        height: other.height,
      });
    });
  }

  private onDragEnd(event: LoadActionEvent): void {
    const originalObject: Load = event.object;
    const isPivot: boolean = event.object.parent.name.includes('pivot');
    if (this.isTest) {
      this.clearFreeSpacesHelperBoxes();
    }
    if (
      originalObject.isTransparent() &&
      shouldRestoreLoadOpacityAfterDrag(originalObject)
    ) {
      originalObject.setOpacity(1);
      originalObject.setTransparency(false);
    }
    let loadX;
    let loadY;
    let loadZ;
    if (isPivot) {
      const eventObjectWorldPosition: Vector3 = new Vector3();
      if (originalObject.yRotation === 0 && originalObject.edgeRotation !== 0) {
        event.object.getWorldPosition(eventObjectWorldPosition);
        loadX = eventObjectWorldPosition.x;
        loadY = eventObjectWorldPosition.y;
        loadZ = eventObjectWorldPosition.z;
      } else if (originalObject.yRotation !== 0 && originalObject.edgeRotation === 0) {
        event.object.parent.getWorldPosition(eventObjectWorldPosition);
        loadX = eventObjectWorldPosition.x + originalObject.rotationDifference.x;
        loadY = eventObjectWorldPosition.y;
        loadZ = eventObjectWorldPosition.z + originalObject.rotationDifference.z;
      } else {
        event.object.parent.parent.getWorldPosition(eventObjectWorldPosition);
        loadX = eventObjectWorldPosition.x + originalObject.rotationDifference.x;
        loadY = eventObjectWorldPosition.y;
        loadZ = eventObjectWorldPosition.z + originalObject.rotationDifference.z;
      }
    } else {
      loadX = event.object.position.x;
      loadY = event.object.position.y;
      loadZ = event.object.position.z;
      event.object.position.x = _.round(loadX);
      event.object.position.y = _.round(loadY);
      event.object.position.z = _.round(loadZ);
    }
    event.object.x = _.round(loadX);
    event.object.y = _.round(loadY);
    event.object.z = _.round(loadZ);
    if (
      loadX === this.draggedLoadPosition.x &&
      loadY === this.draggedLoadPosition.y &&
      loadZ === this.draggedLoadPosition.z
    ) {
      this.renderingContext.cameraControls.enabled = true;
      this.areas = this.areasTemporary;
    } else {
      this.renderingContext.cameraControls.enabled = true;
      if (LoadFactory.size()) {
        LoadFactory.newPosition(event.object, this.draggedLoadPosition);
      }
      const eventData = {
        load: event.object.parent.parent,
        newPosition: {
          x: _.round(loadX / 100, 2),
          y: _.round(loadY / 100, 2),
          z: _.round(loadZ / 100, 2),
        },
        position: {
          x: _.round(this.draggedLoadPosition.x / 100, 2),
          y: _.round(this.draggedLoadPosition.y / 100, 2),
          z: _.round(this.draggedLoadPosition.z / 100, 2),
        },
      };
      this.deactivateDrag();
      this.renderingContext.renderer.domElement.dispatchEvent(
        new CustomEvent('loaddragged', { detail: eventData }),
      );
    }

    if (this.illuminationGroup) {
      this.renderingContext.scene.remove(this.illuminationGroup);
    }
  }

  private clearFreeSpacesHelperBoxes(): void {
    const freeSpacesHelperBoxes: Object3D[] = this.renderingContext.scene.children.filter(
      (child: Object3D) => child.name === 'freeSpaceHelper' || child.name === 'blinkBox',
    );
    freeSpacesHelperBoxes.forEach((box: Object3D) => this.renderingContext.scene.remove(box));
  }

  private onContextMenu(event: LoadActionEvent): void {
    if (this.illuminationGroup) {
      this.renderingContext.scene.remove(this.illuminationGroup);
    }
    const eventData = {
      load: event.object.clone(),
      loadOriginal: event.object,
      isBlocked: event.isBlocked,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      mousePosition: event.mousePosition as { x: number; y: number },
    };
    eventData.load.idGroup = event.object.idGroup;
    eventData.load.name = event.object.name;
    eventData.load.weight = event.object.weight;
    eventData.load.stacking = event.object.stacking;
    eventData.load.color = event.object.color;
    eventData.load.createdAt = event.object.createdAt;
    eventData.load.packed = event.object.packed;
    eventData.load.x = event.object.x;
    eventData.load.y = event.object.y;
    eventData.load.z = event.object.z;
    eventData.load.revert = event.object.revert;
    eventData.load.dragged = event.object.dragged;
    eventData.load.stacked = event.object.stacked;
    eventData.load.maxLoadsOnTop = event.object.maxLoadsOnTop;
    eventData.load.nameTranslated = event.object.nameTranslated;
    eventData.load.showLoadName = event.object.showLoadName;
    eventData.load.covered = event.object.covered;
    eventData.load.unloaded = event.object.unloaded;
    eventData.load.loadType = event.object.loadType;
    eventData.load.position.x = _.round(event.object.position.x / 100, 2);
    eventData.load.position.z = _.round(event.object.position.z / 100, 2);
    eventData.load.position.y = _.round(event.object.position.y / 100, 2);
    eventData.load.length = _.round(event.object.length / 100, 2);
    eventData.load.width = _.round(event.object.width / 100, 2);
    eventData.load.height = _.round(event.object.height / 100, 2);
    this.renderingContext.renderer.domElement.dispatchEvent(
      new CustomEvent('actions', { detail: eventData }),
    );
  }

  private sortAreasByDistance(load: Load): void {
    this.areasThatCanFitLoad
      .sort((a, b) => {
        return b.type - a.type;
      })
      .sort((a: Area, b: Area): number => {
        const offsetA: number =
          Math.abs(this.getOffsetOfLoadOnXInArea(load, a)) +
          Math.abs(this.getOffsetOfLoadOnZInArea(load, a));
        const offsetB: number =
          Math.abs(this.getOffsetOfLoadOnXInArea(load, b)) +
          Math.abs(this.getOffsetOfLoadOnZInArea(load, b));
        if (offsetA !== offsetB) {
          return offsetA - offsetB;
        } else {
          return b.height - a.height;
        }
      });
  }

  private getOffsetOfLoadOnZInArea(load: Load, area: Area): number {
    const withPivot: Load = load.name?.includes('pivot') ? (load.children[0] as Load) : load;
    if (withPivot.position.z >= area.z && withPivot.position.z + withPivot.length <= area.z2()) {
      return 0;
    } else if (withPivot.position.z < area.z) {
      return withPivot.position.z - area.z;
    } else {
      return withPivot.position.z - (area.z2() - withPivot.length);
    }
  }

  private getOffsetOfLoadOnXInArea(load: Load, area: Area): number {
    const withPivot: Load = load.name?.includes('pivot') ? (load.children[0] as Load) : load;
    if (withPivot.position.x >= area.x && withPivot.position.x + withPivot.width <= area.x2()) {
      return 0;
    } else if (withPivot.position.x <= area.x) {
      return withPivot.position.x - area.x;
    } else {
      return withPivot.position.x - (area.x2() - withPivot.width);
    }
  }

  // private render() {
  //   this.renderingContext.renderer.render(
  //     this.renderingContext.scene,
  //     this.renderingContext.camera,
  //   );
  // }
}

